<template>
  <div class="w-full max-w-md mx-auto">
    <!-- Reset Request Form -->
    <form v-if="!emailSent" @submit.prevent="handleForgotPassword" class="space-y-6">
      <div class="text-center">
        <h2 class="text-3xl font-bold text-gray-900">Reset your password</h2>
        <p class="mt-2 text-sm text-gray-600">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="authStore.error" class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">{{ authStore.error }}</h3>
          </div>
        </div>
      </div>

      <!-- Email Field -->
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <div class="mt-1">
          <input
            id="email"
            v-model="form.email"
            name="email"
            type="email"
            autocomplete="email"
            required
            :class="[
              'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm',
              errors.email ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Enter your email address"
          />
          <p v-if="errors.email" class="mt-2 text-sm text-red-600">{{ errors.email }}</p>
        </div>
      </div>

      <!-- Submit Button -->
      <div>
        <button
          type="submit"
          :disabled="authStore.isLoading || !isFormValid"
          class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="authStore.isLoading" class="absolute left-0 inset-y-0 flex items-center pl-3">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {{ authStore.isLoading ? 'Sending...' : 'Send reset link' }}
        </button>
      </div>

      <!-- Back to Login -->
      <div class="text-center">
        <button
          type="button"
          @click="$emit('switch-mode', 'login')"
          class="text-sm text-blue-600 hover:text-blue-500"
        >
          ← Back to sign in
        </button>
      </div>
    </form>

    <!-- Success Message -->
    <div v-else class="text-center space-y-6">
      <div class="flex justify-center">
        <div class="rounded-full bg-[#4ECF84]/10 p-3">
          <svg class="h-8 w-8 text-[#4ECF84]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      
      <div>
        <h2 class="text-3xl font-bold text-gray-900">Check your email</h2>
        <p class="mt-2 text-sm text-gray-600">
          We've sent a password reset link to
          <span class="font-medium text-gray-900">{{ form.email }}</span>
        </p>
      </div>

      <div class="bg-blue-50 rounded-md p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800">
              Didn't receive the email?
            </h3>
            <div class="mt-2 text-sm text-blue-700">
              <ul class="list-disc pl-5 space-y-1">
                <li>Check your spam folder</li>
                <li>Make sure you entered the correct email address</li>
                <li>The link will expire in 24 hours</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Resend Email -->
      <div class="space-y-3">
        <button
          @click="handleResendEmail"
          :disabled="resendCooldown > 0"
          class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend email' }}
        </button>

        <button
          @click="$emit('switch-mode', 'login')"
          class="text-sm text-blue-600 hover:text-blue-500"
        >
          ← Back to sign in
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/store/auth'

const emit = defineEmits(['switch-mode'])
const authStore = useAuthStore()

// Form state
const form = ref({
  email: ''
})

const emailSent = ref(false)
const errors = ref({})
const resendCooldown = ref(0)
let resendTimer = null

// Computed
const isFormValid = computed(() => {
  return form.value.email && form.value.email.includes('@')
})

// Methods
const validateForm = () => {
  errors.value = {}
  
  if (!form.value.email) {
    errors.value.email = 'Email is required'
  } else if (!form.value.email.includes('@')) {
    errors.value.email = 'Please enter a valid email address'
  }
  
  return Object.keys(errors.value).length === 0
}

const handleForgotPassword = async () => {
  // Clear previous errors
  authStore.clearError()
  
  if (!validateForm()) {
    return
  }

  const result = await authStore.forgotPassword(form.value.email)

  if (result.success) {
    emailSent.value = true
    startResendCooldown()
  }
}

const handleResendEmail = async () => {
  const result = await authStore.forgotPassword(form.value.email)
  
  if (result.success) {
    startResendCooldown()
  }
}

const startResendCooldown = () => {
  resendCooldown.value = 60 // 60 seconds cooldown
  
  resendTimer = setInterval(() => {
    resendCooldown.value--
    
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimer)
      resendTimer = null
    }
  }, 1000)
}

onMounted(() => {
  // Focus email field
  const emailInput = document.getElementById('email')
  if (emailInput) {
    emailInput.focus()
  }
})

onUnmounted(() => {
  if (resendTimer) {
    clearInterval(resendTimer)
  }
})
</script>